import { Loading } from '@perch-labs/shrike';
import { range } from 'lodash';
import { useState, useRef, useEffect } from 'react';
import { pdfjs } from 'react-pdf';
import 'react-pdf/dist/esm/Page/AnnotationLayer.css';
import { trackError } from '~/utils/tracking';
import { DocumentWrapper, StyledPage, PdfWrapper } from './scrollable-pdf.style';

interface ScrollablePdfProps {
  pdfUrl: string;
}

pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.js`;

// Checkbox is not rendering without this option due to missing font files
const options = {
  standardFontDataUrl: `https://unpkg.com/pdfjs-dist@${pdfjs.version}/standard_fonts/`,
};

export const ScrollablePdf = ({ pdfUrl }: ScrollablePdfProps) => {
  const [numPages, setNumPages] = useState(0);
  const [loading, setLoading] = useState(false);
  const [width, setWidth] = useState(0);

  const ref = useRef<HTMLDivElement>(null);

  const updateWidth = () => {
    const { current } = ref;
    if (!current) return;

    setWidth(current.clientWidth);
  };

  const onDocumentLoadSuccess = ({ numPages }) => {
    setLoading(false);
    setNumPages(numPages);
  };

  const handleError = error => {
    setLoading(false);
    // ResizeObserver loop notification is a benign browser warning, not a real PDF error
    if (error?.message?.includes('ResizeObserver loop')) return;
    trackError(error); // Sends error to segment
  };

  useEffect(() => {
    const { current } = ref;
    if (!current) return;

    updateWidth();

    // This is needed to handle dynamic resizing of the PDF container,
    // which can cause the PDF to re-render at an incorrect width if not accounted for.
    // The ResizeObserver will trigger whenever the container size changes,
    // allowing us to update the width state accordingly and ensure the PDF renders correctly.
    // This is still not a perfect solution as there can be a flash of incorrectly sized PDF on resize,
    // but it is an improvement over not handling resizes at all.
    let rafId: number;
    const observer = new ResizeObserver(() => {
      cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(updateWidth);
    });
    observer.observe(current);

    return () => {
      observer.disconnect();
      cancelAnimationFrame(rafId);
    };
  }, []);

  return (
    <PdfWrapper ref={ref} className="notranslate">
      <DocumentWrapper
        file={pdfUrl}
        externalLinkTarget="_blank"
        onError={handleError}
        onLoadSuccess={onDocumentLoadSuccess}
        options={options}
      >
        {loading ? (
          <Loading />
        ) : (
          range(1, numPages + 1).map(pageNum => (
            <StyledPage
              pageNumber={pageNum}
              key={pageNum}
              width={width}
              renderAnnotationLayer
              onLoadSuccess={updateWidth}
              onError={handleError}
            />
          ))
        )}
      </DocumentWrapper>
    </PdfWrapper>
  );
};
